import { Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { type FC, type ReactNode } from "react";
import { LEAD_ACTION_TOOLTIPS } from "./lead-actions-copy";

export type MissionBrowseMode = "matched" | "all";
export type MissionAttendanceFilter = "lead" | "group" | "individual" | "all";

type Props = {
  browseMode: MissionBrowseMode;
  attendanceFilter: MissionAttendanceFilter;
  leadAttendance: string | null;
  onBrowseModeChange: (mode: MissionBrowseMode) => void;
  onAttendanceFilterChange: (filter: MissionAttendanceFilter) => void;
};

function TippedToggle({
  value,
  title,
  children,
}: {
  value: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <Tooltip title={title} arrow>
      <ToggleButton value={value}>{children}</ToggleButton>
    </Tooltip>
  );
}

export const LeadMissionTypeSwitcher: FC<Props> = ({
  browseMode,
  attendanceFilter,
  leadAttendance,
  onBrowseModeChange,
  onAttendanceFilterChange,
}) => {
  return (
    <Stack gap={1.5}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          תצוגה:
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={browseMode}
          onChange={(_, v: MissionBrowseMode | null) => v && onBrowseModeChange(v)}
          sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 1.5 } }}
        >
          <TippedToggle value="matched" title={LEAD_ACTION_TOOLTIPS.browseMatched}>
            מתאימות לליד
          </TippedToggle>
          <TippedToggle value="all" title={LEAD_ACTION_TOOLTIPS.browseAll}>
            כל המשימות הפתוחות
          </TippedToggle>
        </ToggleButtonGroup>
      </Stack>

      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          סוג:
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={attendanceFilter}
          onChange={(_, v: MissionAttendanceFilter | null) =>
            v && onAttendanceFilterChange(v)
          }
          sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 1.25 } }}
        >
          <TippedToggle
            value="lead"
            title={LEAD_ACTION_TOOLTIPS.filterByLeadAttendance}
          >
            לפי הליד ({leadAttendance === "GROUP" ? "קבוצה" : "יחיד"})
          </TippedToggle>
          <TippedToggle value="group" title={LEAD_ACTION_TOOLTIPS.filterGroup}>
            קבוצה
          </TippedToggle>
          <TippedToggle
            value="individual"
            title={LEAD_ACTION_TOOLTIPS.filterIndividual}
          >
            יחיד
          </TippedToggle>
          <TippedToggle value="all" title={LEAD_ACTION_TOOLTIPS.filterAllTypes}>
            הכל
          </TippedToggle>
        </ToggleButtonGroup>
      </Stack>
    </Stack>
  );
};

export function resolveIsGroupFilter(
  attendanceFilter: MissionAttendanceFilter,
  _leadAttendance: string | null,
): boolean | undefined {
  if (attendanceFilter === "group") return true;
  if (attendanceFilter === "individual") return false;
  // "lead" and "all" show every team match; attendance preference is soft (offPreferences).
  return undefined;
}
